import { useState } from 'react';
import {
  Chip,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../../../shared/auth/useAuth';
import { downloadAsPdf } from '../../../shared/reports/downloadAsPdf';
import { downloadAsXlsx } from '../../../shared/reports/downloadAsXlsx';
import { buildFilename, formatDate, formatMoney } from '../../../shared/reports/formatters';
import { useGerarContractorInvoices } from '../hooks';
import { RelatorioPageShell } from '../RelatorioPageShell';
import { PeriodoFilter, defaultPeriodoMes } from '../components/PeriodoFilter';
import type { ContractorInvoicesDto, NotaFiscalStatus } from '../types';

const STATUSES: Array<NotaFiscalStatus> = ['NSO', 'SOL', 'PAP', 'APR', 'REP', 'CAN', 'CON'];
const STATUS_LABELS: Record<NotaFiscalStatus, string> = {
  NSO: 'Rascunho',
  SOL: 'Solicitada',
  PAP: 'Aguarda aprovação',
  APR: 'Aprovada',
  REP: 'Reprovada',
  CAN: 'Cancelada',
  CON: 'Conciliada',
};

export function NotasFiscaisColabPage(): JSX.Element {
  const [periodo, setPeriodo] = useState(defaultPeriodoMes());
  const [statusFiltro, setStatusFiltro] = useState<NotaFiscalStatus | ''>('');
  const mutation = useGerarContractorInvoices();
  const { user } = useAuth();
  const usuario = user?.nome ?? '';

  const result = mutation.data;
  const canGenerate = !!periodo.dataInicio && !!periodo.dataFim;

  function gerar(): void {
    mutation.mutate({
      dataInicio: periodo.dataInicio,
      dataFim: periodo.dataFim,
      ...(statusFiltro ? { statusNotaFiscal: statusFiltro } : {}),
    });
  }

  function exportPdf(): void {
    if (!result) return;
    downloadAsPdf(
      buildFilename('nfs-colab', periodo.dataInicio, periodo.dataFim),
      {
        title: 'Notas Fiscais — Colaborador',
        subtitle: `${formatDate(periodo.dataInicio)} a ${formatDate(periodo.dataFim)}`,
        filters: statusFiltro ? [`Status: ${STATUS_LABELS[statusFiltro]}`] : undefined,
        usuario,
      },
      ({ doc, contentStartY }) => {
        autoTable(doc, {
          startY: contentStartY,
          head: [['Colaborador', 'Cliente', 'Período', 'Status', 'Valor', 'Solicitada']],
          body: result.linhas.map((l) => [
            l.colaboradorNome,
            l.clienteNome,
            l.periodo,
            STATUS_LABELS[l.status],
            formatMoney(l.valorReceberFormula),
            formatDate(l.dataSolicitacao),
          ]),
          foot: [
            [
              {
                content: `Total: ${result.totais.count} NFs`,
                colSpan: 4,
                styles: { halign: 'right', fontStyle: 'bold' },
              },
              formatMoney(result.totais.totalValor),
              '',
            ],
          ],
          styles: { fontSize: 8 },
          headStyles: { fillColor: [30, 60, 100] },
          footStyles: { fillColor: [240, 240, 240], textColor: 20, fontStyle: 'bold' },
        });

        if (result.totais.porStatus.length > 0) {
          autoTable(doc, {
            head: [['Status', 'Count', 'Valor']],
            body: result.totais.porStatus.map((s) => [
              STATUS_LABELS[s.status as NotaFiscalStatus] ?? s.status,
              String(s.count),
              formatMoney(s.valor),
            ]),
            styles: { fontSize: 9 },
            headStyles: { fillColor: [80, 80, 80] },
          });
        }
      },
    );
  }

  function exportXlsx(): void {
    if (!result) return;
    downloadAsXlsx(buildFilename('nfs-colab', periodo.dataInicio, periodo.dataFim), [
      {
        name: 'NFs',
        rows: result.linhas.map((l) => ({
          Colaborador: l.colaboradorNome,
          Cliente: l.clienteNome,
          Periodo: l.periodo,
          Status: STATUS_LABELS[l.status],
          Valor: l.valorReceberFormula,
          Solicitada: l.dataSolicitacao,
          Anexada: l.dataAnexo,
          Aprovada: l.dataAprovacao,
        })),
        columns: [
          { key: 'Colaborador', header: 'Colaborador', width: 26 },
          { key: 'Cliente', header: 'Cliente', width: 26 },
          { key: 'Periodo', header: 'Período', width: 14 },
          { key: 'Status', header: 'Status', width: 16 },
          { key: 'Valor', header: 'Valor', format: 'money', width: 14 },
          { key: 'Solicitada', header: 'Solicitada', format: 'date', width: 12 },
          { key: 'Anexada', header: 'Anexada', format: 'date', width: 12 },
          { key: 'Aprovada', header: 'Aprovada', format: 'date', width: 12 },
        ],
      },
      {
        name: 'Por status',
        rows: result.totais.porStatus.map((s) => ({
          Status: STATUS_LABELS[s.status as NotaFiscalStatus] ?? s.status,
          Count: s.count,
          Valor: s.valor,
        })),
        columns: [
          { key: 'Status', header: 'Status', width: 20 },
          { key: 'Count', header: 'Count', format: 'integer', width: 10 },
          { key: 'Valor', header: 'Valor', format: 'money', width: 14 },
        ],
      },
    ]);
  }

  return (
    <RelatorioPageShell
      title="Notas Fiscais — Colaborador"
      subtitle="NFs recebidas de colaboradores PJ no período."
      filters={
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <PeriodoFilter
            dataInicio={periodo.dataInicio}
            dataFim={periodo.dataFim}
            onChange={setPeriodo}
            disabled={mutation.isPending}
          />
          <TextField
            select
            label="Status"
            value={statusFiltro}
            onChange={(e) => setStatusFiltro(e.target.value as NotaFiscalStatus | '')}
            disabled={mutation.isPending}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">Todos</MenuItem>
            {STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {STATUS_LABELS[s]}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      }
      canGenerate={canGenerate}
      onGenerate={gerar}
      generating={mutation.isPending}
      hasResult={!!result}
      error={mutation.isError ? mutation.error : null}
      emptyMessage="Escolha o período e clique em Gerar."
      onExportPdf={exportPdf}
      onExportXlsx={exportXlsx}
      preview={result ? <Preview result={result} /> : null}
    />
  );
}

function Preview({ result }: { result: ContractorInvoicesDto }): JSX.Element {
  return (
    <>
      <Stack direction="row" spacing={3} sx={{ mb: 2 }} flexWrap="wrap">
        <ResumoItem label="NFs no período" value={String(result.totais.count)} />
        <ResumoItem label="Valor total" value={formatMoney(result.totais.totalValor)} />
      </Stack>

      {result.linhas.length === 0 ? (
        <Typography color="text.secondary">Nenhuma NF no período.</Typography>
      ) : (
        <TableContainer sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Colaborador</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Período</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell>Solicitada</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.linhas.map((l) => (
                <TableRow key={l.id}>
                  <TableCell>{l.colaboradorNome}</TableCell>
                  <TableCell>{l.clienteNome}</TableCell>
                  <TableCell>{l.periodo}</TableCell>
                  <TableCell>
                    <Chip size="small" label={STATUS_LABELS[l.status]} />
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 500 }}>
                    {formatMoney(l.valorReceberFormula)}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>
                    {formatDate(l.dataSolicitacao)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {result.totais.porStatus.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ mt: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}
          flexWrap="wrap"
        >
          {result.totais.porStatus.map((s) => (
            <Chip
              key={s.status}
              label={`${STATUS_LABELS[s.status as NotaFiscalStatus] ?? s.status}: ${s.count} · ${formatMoney(s.valor)}`}
              variant="outlined"
            />
          ))}
        </Stack>
      )}
    </>
  );
}

function ResumoItem({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Stack spacing={0}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </Stack>
  );
}
